import { Stack, XStack } from '@lyra/core/components'
import SpanText from '@lyra/core/components/SpanText'
import VideoText from '@lyra/core/components/VideoText'
import formatPercentage from '@lyra/core/utils/formatPercentage'
import formatUSD from '@lyra/core/utils/formatUSD'
import { MarketId } from '@lyra/web/constants/markets'
import useMarkets from '@lyra/web/hooks/useMarkets'
import { forwardRef, Ref, useEffect, useRef, useState } from 'react'

type Props = {
  marketId: MarketId
}

const FLASH_DURATION = 500

const TradeOptionTableRowIndicator = forwardRef(function TradeOptionTableRowIndicator(
  { marketId }: Props,
  ref: Ref<HTMLElement>
) {
  const market = useMarkets()[marketId]
  const spotPrice = market.spotPrice
  const spotPrice24hPctChange = market.spotPrice24hPctChange
  const lastSpotPrice = useRef(spotPrice)

  const [flash, setFlash] = useState(false)

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined = undefined
    if (spotPrice && spotPrice !== lastSpotPrice.current) {
      setFlash(true)
      timeout = setTimeout(() => setFlash(false), FLASH_DURATION)
    } else {
      setFlash(false)
    }
    lastSpotPrice.current = spotPrice
    return () => clearTimeout(timeout)
  }, [spotPrice])

  return (
    <Stack ref={ref} alignItems="center" width="100%">
      <Stack
        style={{
          position: 'absolute',
          right: 0,
          left: 0,
          top: 15,
          height: 1,
        }}
        backgroundColor="$hairline"
      />
      <XStack
        style={{
          borderRadius: '$1',
          position: 'relative',
        }}
        paddingVertical="$1"
        paddingHorizontal="$1.5"
        borderColor="$hairline"
        borderWidth={1}
        backgroundColor="$appBg"
      >
        <VideoText color={flash ? (spotPrice24hPctChange >= 0 ? 'green' : 'red') : 'primary'}>
          {marketId} {formatUSD(market.spotPrice)}&nbsp;
          <SpanText color={spotPrice24hPctChange >= 0 ? 'green' : 'red'}>
            {spotPrice24hPctChange >= 0 ? '▲' : '▼'}&nbsp;
            {formatPercentage(Math.abs(spotPrice24hPctChange), { showSign: false, dps: 2 })}
          </SpanText>
        </VideoText>
      </XStack>
    </Stack>
  )
})

export default TradeOptionTableRowIndicator
